import React from 'react';
import Icon from './atoms/Icon';

// Modern Template (Default)
export const ModernTemplate = ({ resumeData }) => {
  if (!resumeData) return null;

  const { personal_info, summary, experience, projects, skills, education, certifications } = resumeData;

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8 border-b pb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{personal_info?.name || 'Your Name'}</h1>
        <div className="text-gray-600 text-sm">
          {[
            personal_info?.email && `email${personal_info.email}`,
            personal_info?.phone && `phone${personal_info.phone}`,
            personal_info?.location && `location_on${personal_info.location}`,
            personal_info?.linkedin && 'linkLinkedIn Profile'
          ].filter(Boolean).join(' | ')}
        </div>
      </div>

      {/* Professional Summary */}
      {summary && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3 border-b-2 border-blue-500 pb-1">
            Professional Summary
          </h2>
          <p className="text-gray-700 leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Experience */}
      {experience && experience.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-500 pb-1">
            Professional Experience
          </h2>
          {experience.map((exp, index) => (
            <div key={index} className="mb-6">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{exp.title}</h3>
                  <p className="text-blue-600 font-medium">{exp.company}</p>
                </div>
                <div className="text-right text-gray-600">
                  <p className="font-medium">{exp.duration}</p>
                  {exp.location && <p className="text-sm">{exp.location}</p>}
                </div>
              </div>
              {exp.description && (
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  {exp.description.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects && projects.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-500 pb-1">
            Key Projects
          </h2>
          {projects.map((project, index) => (
            <div key={index} className="mb-6">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{project.title}</h3>
                {project.duration && <span className="text-gray-600">{project.duration}</span>}
              </div>
              <p className="text-gray-700 mb-2">{project.description}</p>
              {project.technologies && (
                <div className="mb-2">
                  <span className="text-sm font-medium text-gray-600">Technologies: </span>
                  <span className="text-sm text-gray-700">{project.technologies.join(', ')}</span>
                </div>
              )}
              {project.achievements && (
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  {project.achievements.map((achievement, i) => (
                    <li key={i} className="text-sm">{achievement}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {skills && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-500 pb-1">
            Skills
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {skills.technical && skills.technical.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Technical Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.technical.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {skills.programming && skills.programming.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Programming Languages</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.programming.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {skills.tools && skills.tools.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Tools & Technologies</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.tools.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {skills.soft_skills && skills.soft_skills.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Soft Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.soft_skills.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Education */}
      {education && education.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-500 pb-1">
            Education
          </h2>
          {education.map((edu, index) => (
            <div key={index} className="mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{edu.degree}</h3>
                  <p className="text-blue-600">{edu.institution}</p>
                  {edu.relevant_coursework && edu.relevant_coursework.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      Relevant Coursework: {edu.relevant_coursework.join(', ')}
                    </p>
                  )}
                </div>
                <div className="text-right text-gray-600">
                  <p>{edu.year}</p>
                  {edu.gpa && <p className="text-sm">GPA: {edu.gpa}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Certifications */}
      {certifications && certifications.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b-2 border-blue-500 pb-1">
            Certifications
          </h2>
          {certifications.map((cert, index) => (
            <div key={index} className="mb-3">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-900">{cert.name}</h3>
                  <p className="text-blue-600">{cert.issuer}</p>
                  {cert.credential_id && (
                    <p className="text-sm text-gray-600">ID: {cert.credential_id}</p>
                  )}
                </div>
                <span className="text-gray-600">{cert.year}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Professional Template
export const ProfessionalTemplate = ({ resumeData }) => {
  if (!resumeData) return null;

  const { personal_info, summary, experience, projects, skills, education, certifications } = resumeData;

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto border-l-4 border-gray-800">
      {/* Header */}
      <div className="mb-8 pb-6 border-b-2 border-gray-200">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">{personal_info?.name || 'Your Name'}</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-gray-600">
          {personal_info?.email && (
            <div className="flex items-center gap-2">
              <Icon name="email" size={16} />
              <span>{personal_info.email}</span>
            </div>
          )}
          {personal_info?.phone && (
            <div className="flex items-center gap-2">
              <Icon name="phone" size={16} />
              <span>{personal_info.phone}</span>
            </div>
          )}
          {personal_info?.location && (
            <div className="flex items-center gap-2">
              <Icon name="location_on" size={16} />
              <span>{personal_info.location}</span>
            </div>
          )}
          {personal_info?.linkedin && (
            <div className="flex items-center gap-2">
              <Icon name="link" size={16} />
              <span>LinkedIn Profile</span>
            </div>
          )}
        </div>
      </div>

      {/* Professional Summary */}
      {summary && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3 uppercase tracking-wide">
            Professional Summary
          </h2>
          <p className="text-gray-700 leading-relaxed text-justify">{summary}</p>
        </div>
      )}

      {/* Experience */}
      {experience && experience.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">
            Professional Experience
          </h2>
          {experience.map((exp, index) => (
            <div key={index} className="mb-6 border-l-2 border-gray-300 pl-4">
              <div className="mb-2">
                <h3 className="text-lg font-bold text-gray-900">{exp.title}</h3>
                <div className="flex justify-between items-center">
                  <p className="text-gray-800 font-medium">{exp.company}</p>
                  <p className="text-gray-600 font-medium">{exp.duration}</p>
                </div>
                {exp.location && <p className="text-gray-600 text-sm">{exp.location}</p>}
              </div>
              {exp.description && (
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  {exp.description.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects && projects.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">
            Key Projects
          </h2>
          {projects.map((project, index) => (
            <div key={index} className="mb-6 border-l-2 border-gray-300 pl-4">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-bold text-gray-900">{project.title}</h3>
                {project.duration && <span className="text-gray-600 font-medium">{project.duration}</span>}
              </div>
              <p className="text-gray-700 mb-2">{project.description}</p>
              {project.technologies && (
                <div className="mb-2">
                  <span className="text-sm font-bold text-gray-800">Technologies: </span>
                  <span className="text-sm text-gray-700">{project.technologies.join(', ')}</span>
                </div>
              )}
              {project.achievements && (
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  {project.achievements.map((achievement, i) => (
                    <li key={i} className="text-sm">{achievement}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {skills && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">
            Core Competencies
          </h2>
          <div className="space-y-3">
            {skills.technical && skills.technical.length > 0 && (
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Technical Skills:</h4>
                <p className="text-gray-700">{skills.technical.join(' • ')}</p>
              </div>
            )}
            {skills.programming && skills.programming.length > 0 && (
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Programming Languages:</h4>
                <p className="text-gray-700">{skills.programming.join(' • ')}</p>
              </div>
            )}
            {skills.tools && skills.tools.length > 0 && (
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Tools & Technologies:</h4>
                <p className="text-gray-700">{skills.tools.join(' • ')}</p>
              </div>
            )}
            {skills.soft_skills && skills.soft_skills.length > 0 && (
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Soft Skills:</h4>
                <p className="text-gray-700">{skills.soft_skills.join(' • ')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Education */}
      {education && education.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">
            Education
          </h2>
          {education.map((edu, index) => (
            <div key={index} className="mb-4 border-l-2 border-gray-300 pl-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{edu.degree}</h3>
                  <p className="text-gray-800">{edu.institution}</p>
                  {edu.relevant_coursework && edu.relevant_coursework.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Relevant Coursework:</span> {edu.relevant_coursework.join(', ')}
                    </p>
                  )}
                </div>
                <div className="text-right text-gray-600">
                  <p className="font-medium">{edu.year}</p>
                  {edu.gpa && <p className="text-sm">GPA: {edu.gpa}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Certifications */}
      {certifications && certifications.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 uppercase tracking-wide">
            Certifications
          </h2>
          {certifications.map((cert, index) => (
            <div key={index} className="mb-3 border-l-2 border-gray-300 pl-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-gray-900">{cert.name}</h3>
                  <p className="text-gray-800">{cert.issuer}</p>
                  {cert.credential_id && (
                    <p className="text-sm text-gray-600">Credential ID: {cert.credential_id}</p>
                  )}
                </div>
                <span className="text-gray-600 font-medium">{cert.year}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Creative Template
export const CreativeTemplate = ({ resumeData }) => {
  if (!resumeData) return null;

  const { personal_info, summary, experience, projects, skills, education, certifications } = resumeData;

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8 bg-white rounded-lg p-6 shadow-md">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-3">
          {personal_info?.name || 'Your Name'}
        </h1>
        <div className="flex flex-wrap justify-center gap-4 text-gray-600">
          {personal_info?.email && (
            <span className="flex items-center gap-1 bg-purple-100 px-3 py-1 rounded-full">
              <Icon name="email" size={16} />
              {personal_info.email}
            </span>
          )}
          {personal_info?.phone && (
            <span className="flex items-center gap-1 bg-blue-100 px-3 py-1 rounded-full">
              <Icon name="phone" size={16} />
              {personal_info.phone}
            </span>
          )}
          {personal_info?.location && (
            <span className="flex items-center gap-1 bg-green-100 px-3 py-1 rounded-full">
              <Icon name="location_on" size={16} />
              {personal_info.location}
            </span>
          )}
          {personal_info?.linkedin && (
            <span className="flex items-center gap-1 bg-indigo-100 px-3 py-1 rounded-full">
              <Icon name="link" size={16} />
              LinkedIn
            </span>
          )}
        </div>
      </div>

      {/* Professional Summary */}
      {summary && (
        <div className="mb-8 bg-white rounded-lg p-6 shadow-md">
          <h2 className="text-2xl font-bold text-purple-600 mb-3 flex items-center gap-2">
            <Icon name="person" className="text-purple-600" />
            About Me
          </h2>
          <p className="text-gray-700 leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Experience */}
      {experience && experience.length > 0 && (
        <div className="mb-8 bg-white rounded-lg p-6 shadow-md">
          <h2 className="text-2xl font-bold text-blue-600 mb-4 flex items-center gap-2">
            <Icon name="work" className="text-blue-600" />
            Experience
          </h2>
          {experience.map((exp, index) => (
            <div key={index} className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{exp.title}</h3>
                  <p className="text-blue-600 font-medium">{exp.company}</p>
                </div>
                <div className="text-right text-gray-600">
                  <p className="font-medium bg-white px-2 py-1 rounded">{exp.duration}</p>
                  {exp.location && <p className="text-sm mt-1">{exp.location}</p>}
                </div>
              </div>
              {exp.description && (
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  {exp.description.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects && projects.length > 0 && (
        <div className="mb-8 bg-white rounded-lg p-6 shadow-md">
          <h2 className="text-2xl font-bold text-green-600 mb-4 flex items-center gap-2">
            <Icon name="code" className="text-green-600" />
            Projects
          </h2>
          {projects.map((project, index) => (
            <div key={index} className="mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-bold text-gray-900">{project.title}</h3>
                {project.duration && (
                  <span className="text-gray-600 bg-white px-2 py-1 rounded">{project.duration}</span>
                )}
              </div>
              <p className="text-gray-700 mb-2">{project.description}</p>
              {project.technologies && (
                <div className="mb-2">
                  <div className="flex flex-wrap gap-1">
                    {project.technologies.map((tech, i) => (
                      <span key={i} className="px-2 py-1 bg-green-200 text-green-800 rounded-full text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {project.achievements && (
                <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                  {project.achievements.map((achievement, i) => (
                    <li key={i} className="text-sm">{achievement}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {skills && (
        <div className="mb-8 bg-white rounded-lg p-6 shadow-md">
          <h2 className="text-2xl font-bold text-indigo-600 mb-4 flex items-center gap-2">
            <Icon name="star" className="text-indigo-600" />
            Skills
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {skills.technical && skills.technical.length > 0 && (
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
                <h4 className="font-bold text-blue-800 mb-3">Technical Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.technical.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-blue-200 text-blue-800 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {skills.programming && skills.programming.length > 0 && (
              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                <h4 className="font-bold text-green-800 mb-3">Programming</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.programming.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {skills.tools && skills.tools.length > 0 && (
              <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                <h4 className="font-bold text-purple-800 mb-3">Tools & Tech</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.tools.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-purple-200 text-purple-800 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {skills.soft_skills && skills.soft_skills.length > 0 && (
              <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg">
                <h4 className="font-bold text-orange-800 mb-3">Soft Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {skills.soft_skills.map((skill, i) => (
                    <span key={i} className="px-3 py-1 bg-orange-200 text-orange-800 rounded-full text-sm font-medium">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Education */}
      {education && education.length > 0 && (
        <div className="mb-8 bg-white rounded-lg p-6 shadow-md">
          <h2 className="text-2xl font-bold text-purple-600 mb-4 flex items-center gap-2">
            <Icon name="school" className="text-purple-600" />
            Education
          </h2>
          {education.map((edu, index) => (
            <div key={index} className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{edu.degree}</h3>
                  <p className="text-purple-600 font-medium">{edu.institution}</p>
                  {edu.relevant_coursework && edu.relevant_coursework.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Coursework:</span> {edu.relevant_coursework.join(', ')}
                    </p>
                  )}
                </div>
                <div className="text-right text-gray-600">
                  <p className="font-medium bg-white px-2 py-1 rounded">{edu.year}</p>
                  {edu.gpa && <p className="text-sm mt-1">GPA: {edu.gpa}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Certifications */}
      {certifications && certifications.length > 0 && (
        <div className="mb-8 bg-white rounded-lg p-6 shadow-md">
          <h2 className="text-2xl font-bold text-green-600 mb-4 flex items-center gap-2">
            <Icon name="verified" className="text-green-600" />
            Certifications
          </h2>
          {certifications.map((cert, index) => (
            <div key={index} className="mb-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-gray-900">{cert.name}</h3>
                  <p className="text-green-600 font-medium">{cert.issuer}</p>
                  {cert.credential_id && (
                    <p className="text-sm text-gray-600">ID: {cert.credential_id}</p>
                  )}
                </div>
                <span className="text-gray-600 font-medium bg-white px-2 py-1 rounded">{cert.year}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Minimal Template
export const MinimalTemplate = ({ resumeData }) => {
  if (!resumeData) return null;

  const { personal_info, summary, experience, projects, skills, education, certifications } = resumeData;

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto font-serif">
      {/* Header */}
      <div className="text-left mb-8 pb-4 border-b border-gray-300">
        <h1 className="text-3xl font-light text-gray-900 mb-2">{personal_info?.name || 'Your Name'}</h1>
        <div className="text-gray-600 space-y-1">
          {personal_info?.email && <p>{personal_info.email}</p>}
          {personal_info?.phone && <p>{personal_info.phone}</p>}
          {personal_info?.location && <p>{personal_info.location}</p>}
          {personal_info?.linkedin && <p>LinkedIn Profile Available</p>}
        </div>
      </div>

      {/* Professional Summary */}
      {summary && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-3 border-b border-gray-200 pb-1">
            Summary
          </h2>
          <p className="text-gray-700 leading-relaxed">{summary}</p>
        </div>
      )}

      {/* Experience */}
      {experience && experience.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-1">
            Experience
          </h2>
          {experience.map((exp, index) => (
            <div key={index} className="mb-6">
              <div className="flex justify-between items-baseline mb-1">
                <h3 className="text-base font-medium text-gray-900">{exp.title}</h3>
                <span className="text-sm text-gray-600">{exp.duration}</span>
              </div>
              <p className="text-gray-700 mb-2">{exp.company}{exp.location && `, ${exp.location}`}</p>
              {exp.description && (
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  {exp.description.map((desc, i) => (
                    <li key={i}>{desc}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Projects */}
      {projects && projects.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-1">
            Projects
          </h2>
          {projects.map((project, index) => (
            <div key={index} className="mb-6">
              <div className="flex justify-between items-baseline mb-1">
                <h3 className="text-base font-medium text-gray-900">{project.title}</h3>
                {project.duration && <span className="text-sm text-gray-600">{project.duration}</span>}
              </div>
              <p className="text-gray-700 mb-2 text-sm">{project.description}</p>
              {project.technologies && (
                <p className="text-xs text-gray-600 mb-2">
                  <span className="font-medium">Technologies:</span> {project.technologies.join(', ')}
                </p>
              )}
              {project.achievements && (
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  {project.achievements.map((achievement, i) => (
                    <li key={i}>{achievement}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Skills */}
      {skills && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-1">
            Skills
          </h2>
          <div className="space-y-2 text-sm">
            {skills.technical && skills.technical.length > 0 && (
              <p><span className="font-medium text-gray-800">Technical:</span> {skills.technical.join(', ')}</p>
            )}
            {skills.programming && skills.programming.length > 0 && (
              <p><span className="font-medium text-gray-800">Programming:</span> {skills.programming.join(', ')}</p>
            )}
            {skills.tools && skills.tools.length > 0 && (
              <p><span className="font-medium text-gray-800">Tools:</span> {skills.tools.join(', ')}</p>
            )}
            {skills.soft_skills && skills.soft_skills.length > 0 && (
              <p><span className="font-medium text-gray-800">Soft Skills:</span> {skills.soft_skills.join(', ')}</p>
            )}
          </div>
        </div>
      )}

      {/* Education */}
      {education && education.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-1">
            Education
          </h2>
          {education.map((edu, index) => (
            <div key={index} className="mb-4">
              <div className="flex justify-between items-baseline">
                <div>
                  <h3 className="text-base font-medium text-gray-900">{edu.degree}</h3>
                  <p className="text-gray-700">{edu.institution}</p>
                  {edu.relevant_coursework && edu.relevant_coursework.length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">
                      Coursework: {edu.relevant_coursework.join(', ')}
                    </p>
                  )}
                </div>
                <div className="text-right text-gray-600 text-sm">
                  <p>{edu.year}</p>
                  {edu.gpa && <p>GPA: {edu.gpa}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Certifications */}
      {certifications && certifications.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4 border-b border-gray-200 pb-1">
            Certifications
          </h2>
          {certifications.map((cert, index) => (
            <div key={index} className="mb-3">
              <div className="flex justify-between items-baseline">
                <div>
                  <h3 className="text-base font-medium text-gray-900">{cert.name}</h3>
                  <p className="text-gray-700">{cert.issuer}</p>
                  {cert.credential_id && (
                    <p className="text-xs text-gray-600">ID: {cert.credential_id}</p>
                  )}
                </div>
                <span className="text-gray-600 text-sm">{cert.year}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const RESUME_TEMPLATES = {
  modern: {
    name: 'Modern',
    description: 'Clean and contemporary design with color accents',
    component: ModernTemplate,
    preview: '/api/placeholder/200/280'
  },
  professional: {
    name: 'Professional',
    description: 'Traditional corporate style with clean lines',
    component: ProfessionalTemplate,
    preview: '/api/placeholder/200/280'
  },
  creative: {
    name: 'Creative',
    description: 'Colorful and engaging design for creative roles',
    component: CreativeTemplate,
    preview: '/api/placeholder/200/280'
  },
  minimal: {
    name: 'Minimal',
    description: 'Simple and elegant serif typography',
    component: MinimalTemplate,
    preview: '/api/placeholder/200/280'
  }
};
